import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { contactApi, contactQueryKeys } from "service/crm/api/contact";
import { NonAssociatedCompanyContactParams } from "types/crm/company";
import { companyQueryKeys } from "../api/company";

interface UseQueryOptions {
  enabled?: boolean;
}

/** Contacts associated with a company — GET /crm/companies/{companyUuid}/contacts */
export const useCompanyContacts = (
  companyUuid: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: contactQueryKeys.byCompany(companyUuid),
    queryFn: () => contactApi.getContactsByCompany(companyUuid),
    enabled: (options.enabled ?? true) && !!companyUuid,
  });
};

/**
 * Org contacts not yet associated with the company — for the add-search UI.
 * Search is server-side; pages are appended as the list is scrolled.
 */
export const useNonAssociatedCompanyContacts = (
  {
    companyUuid,
    search,
    limit,
  }: Omit<NonAssociatedCompanyContactParams, "page">,
  options: UseQueryOptions = {},
) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: contactQueryKeys.nonAssociatedByCompany(companyUuid, {
      search,
      limit,
    }),
    queryFn: ({ pageParam }) =>
      contactApi.getNonAssociatedCompanyContacts({
        companyUuid,
        search,
        limit,
        page: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: (options.enabled ?? true) && !!companyUuid,
  });

  return {
    contacts: data?.pages.flatMap((p) => p.content) ?? [],
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements: data?.pages[0]?.totalElements ?? 0,
  };
};

export const useAssociateCompanyContactMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: contactApi.associateContactWithCompany,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: contactQueryKeys.byCompany(variables.companyUuid),
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};
